import React, { useEffect, useRef, useState } from "react";
import { X, Plus, Upload, Paperclip, FileText, Check } from "lucide-react";
import { DOC_TYPES } from "../../constants";
import { fmtEmailTime, guessDocType, readFileAsAttachment } from "../../utils";
import { DownloadLink } from "../common/DownloadLink";
import type { SimAttachment, SimEmail } from "../../types";

export interface EmailEditorState {
  corpusId: string;
  email: SimEmail;
  isNew: boolean;
}

interface EmailFormState {
  fromName: string;
  from: string;
  to: string;
  cc: string;
  subject: string;
  time: string;
  raw: string;
  attachments: SimAttachment[];
}

export interface EmailEditorProps {
  st: EmailEditorState;
  onClose: () => void;
  onSave: (corpusId: string, email: SimEmail, isNew: boolean) => void;
  onDelete: (corpusId: string, emailId: number) => void;
}

/* Modal for adding or editing an email inside a corpus. Lets the user paste
   the raw message, attach files, and edit per-attachment doc-type/version. */
export function EmailEditor({ st, onClose, onSave, onDelete }: EmailEditorProps): JSX.Element {
  const e = st.email;
  const [f, setF] = useState<EmailFormState>({
    fromName: e.fromName || "",
    from: e.from || "",
    to: (e.to || []).join(", "),
    cc: (e.cc || []).join(", "),
    subject: e.subject || "",
    time: fmtEmailTime(e.time),
    raw: e.raw || "",
    attachments: (e.attachments || []).map((a) => ({ ...a })),
  });
  const initialRef = useRef<string>("");
  const fRef = useRef(f);
  fRef.current = f;
  if (!initialRef.current) initialRef.current = JSON.stringify(f);
  const requestClose = (): void => {
    const dirty = JSON.stringify(fRef.current) !== initialRef.current;
    if (dirty && !window.confirm("Discard unsaved changes to this email?")) return;
    onClose();
  };
  useEffect(() => {
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") {
        ev.stopPropagation();
        requestClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const up = <K extends keyof EmailFormState>(k: K, v: EmailFormState[K]): void =>
    setF((s) => ({ ...s, [k]: v }));
  const updateAtt = (i: number, k: keyof SimAttachment, v: string | number): void =>
    setF((s) => ({
      ...s,
      attachments: s.attachments.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)),
    }));
  const patchAtt = (i: number, patch: Partial<SimAttachment>): void =>
    setF((s) => ({
      ...s,
      attachments: s.attachments.map((a, idx) => (idx === i ? { ...a, ...patch } : a)),
    }));
  const addAtt = (): void =>
    setF((s) => ({
      ...s,
      attachments: [
        ...s.attachments,
        { filename: "new_document.docx", doc_type: "term_sheet", version: 1, content: "" } as SimAttachment,
      ],
    }));
  const delAtt = (i: number): void =>
    setF((s) => ({ ...s, attachments: s.attachments.filter((_, idx) => idx !== i) }));
  const addUploaded = async (files: FileList | File[]): Promise<void> => {
    const ups = await Promise.all(Array.from(files).map(readFileAsAttachment));
    setF((s) => ({
      ...s,
      attachments: [
        ...s.attachments,
        ...ups.map((u) => ({
          filename: u.filename,
          doc_type: guessDocType(u.filename),
          version: 1,
          content: "",
          fileData: u.fileData,
        }) as SimAttachment),
      ],
    }));
  };
  const replaceFile = async (i: number, file: File): Promise<void> => {
    const u = await readFileAsAttachment(file);
    patchAtt(i, { filename: u.filename, fileData: u.fileData });
  };
  const save = (): void =>
    onSave(
      st.corpusId,
      {
        ...e,
        fromName: f.fromName,
        from: f.from,
        to: f.to.split(",").map((x) => x.trim()).filter(Boolean),
        cc: f.cc.split(",").map((x) => x.trim()).filter(Boolean),
        subject: f.subject,
        time: f.time,
        raw: f.raw,
        attachments: f.attachments,
      },
      st.isNew,
    );
  return (
    <div className="modal" onClick={requestClose}>
      <div
        className="mbox"
        onClick={(ev) => ev.stopPropagation()}
        style={{ width: "min(760px,100%)" }}
      >
        <div className="mbhd">
          <div style={{ flex: 1 }}>
            <div className="mt">{st.isNew ? "Add email" : "Edit email #" + e.id}</div>
            <div className="ms">
              Editing changes the raw message; the spine re-derives the delta on the next run.
            </div>
          </div>
          <button className="icon-btn" onClick={requestClose}>
            <X size={17} />
          </button>
        </div>
        <div className="scroll" style={{ padding: 18 }}>
          <div className="fieldr">
            <label>From</label>
            <input
              className="inp"
              style={{ flex: 1 }}
              placeholder="Name"
              value={f.fromName}
              onChange={(t) => up("fromName", t.target.value)}
            />
            <input
              className="inp mono"
              style={{ flex: 1.3, fontSize: 12 }}
              placeholder="email@domain"
              value={f.from}
              onChange={(t) => up("from", t.target.value)}
            />
          </div>
          <div className="fieldr">
            <label>To</label>
            <input
              className="inp mono"
              style={{ flex: 1, fontSize: 12 }}
              placeholder="comma-separated"
              value={f.to}
              onChange={(t) => up("to", t.target.value)}
            />
          </div>
          <div className="fieldr">
            <label>Cc</label>
            <input
              className="inp mono"
              style={{ flex: 1, fontSize: 12 }}
              placeholder="comma-separated"
              value={f.cc}
              onChange={(t) => up("cc", t.target.value)}
            />
          </div>
          <div className="fieldr">
            <label>Subj</label>
            <input
              className="inp"
              style={{ flex: 1 }}
              value={f.subject}
              onChange={(t) => up("subject", t.target.value)}
            />
          </div>
          <div className="fieldr">
            <label>Date</label>
            <input
              className="inp mono"
              style={{ flex: 1, fontSize: 12 }}
              value={f.time}
              onChange={(t) => up("time", t.target.value)}
            />
          </div>
          <div className="up" style={{ margin: "6px 0 4px" }}>
            Raw body (include quoted thread, signature & confidentiality footer as sent)
          </div>
          <textarea
            className="inp tarea"
            style={{ minHeight: 200 }}
            value={f.raw}
            onChange={(t) => up("raw", t.target.value)}
          />
          <div className="attsec">
            <div className="attshd">
              <Paperclip size={13} /> Attachments · {f.attachments.length}
              <span className="spacer" />
              <label className="upbtn" title="Upload a .docx or .pdf file">
                <Upload size={13} /> Upload file
                <input
                  type="file"
                  accept=".docx,.pdf,.xlsx,.doc"
                  multiple
                  style={{ display: "none" }}
                  onChange={(ev) => {
                    const fl = ev.target.files;
                    ev.target.value = "";
                    if (fl && fl.length) addUploaded(fl);
                  }}
                />
              </label>
              <button className="btn btn-ghost btn-sm" onClick={addAtt}>
                <Plus size={13} /> Add attachment
              </button>
            </div>
            {f.attachments.length === 0 && (
              <div className="empty" style={{ padding: "14px 6px" }}>
                No attachments on this email.
              </div>
            )}
            {f.attachments.map((a, i) => (
              <div key={i} className="attcard">
                <div className="attcardhd" style={{ cursor: "default" }}>
                  <FileText size={12} color="var(--muted)" />
                  <input
                    className="inp mono"
                    style={{ flex: 1, fontSize: 12, height: 26, padding: "4px 8px" }}
                    value={a.filename}
                    onChange={(t) => updateAtt(i, "filename", t.target.value)}
                  />
                  <select
                    className="sel"
                    style={{ flex: "none", height: 26, fontSize: 12, padding: "2px 6px" }}
                    value={a.doc_type}
                    onChange={(t) => updateAtt(i, "doc_type", t.target.value)}
                  >
                    {DOC_TYPES.map(([id, lab]) => (
                      <option key={id} value={id}>
                        {lab}
                      </option>
                    ))}
                  </select>
                  <input
                    className="inp mono"
                    type="number"
                    min="1"
                    style={{ width: 60, fontSize: 12, height: 26, padding: "4px 6px" }}
                    value={a.version}
                    onChange={(t) => updateAtt(i, "version", Number(t.target.value) || 1)}
                  />
                  {a.fileData && (
                    <span className="fileok">
                      <Check size={11} />
                      file
                    </span>
                  )}
                  {(a.fileData || a.content) && (
                    <DownloadLink doc={a} size={13} variant="ghost" />
                  )}
                  <label
                    className="icon-btn"
                    title="Replace with uploaded file"
                    style={{ cursor: "pointer" }}
                  >
                    <Upload size={14} />
                    <input
                      type="file"
                      accept=".docx,.pdf,.xlsx,.doc"
                      style={{ display: "none" }}
                      onChange={(ev) => {
                        const fi = ev.target.files && ev.target.files[0];
                        ev.target.value = "";
                        if (fi) replaceFile(i, fi);
                      }}
                    />
                  </label>
                  <button
                    className="icon-btn"
                    onClick={() => delAtt(i)}
                    title="Delete attachment"
                  >
                    <X size={14} />
                  </button>
                </div>
                <textarea
                  className="inp"
                  style={{
                    width: "100%",
                    minHeight: 140,
                    maxHeight: 280,
                    fontFamily: "var(--mono)",
                    fontSize: 11,
                    lineHeight: 1.55,
                    border: "none",
                    borderTop: "1px solid var(--bd)",
                    borderRadius: 0,
                  }}
                  placeholder="Paste full document content here…"
                  value={a.content || ""}
                  onChange={(t) => updateAtt(i, "content", t.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="mbft">
          {!st.isNew && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => onDelete(st.corpusId, e.id)}
            >
              <X size={14} /> Delete
            </button>
          )}
          <span className="spacer" />
          <button className="btn btn-ghost btn-sm" onClick={requestClose}>
            Cancel
          </button>
          <button className="btn btn-primary btn-sm" onClick={save}>
            <Check size={14} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
